import { ZeroPriceType } from '../../types/enums';

/**
 * Per-area configuration of a 0 EUR promotional article.
 *
 * `active` releases the article for ordering in that area — the pre-existing flag, previously
 * carried by the single `zeroPriceType` string. `limit` is the maximum quantity per cart
 * (per resident cart in B2C, per B2B order in B2B); `null` means unlimited.
 */
export interface ZeroPriceSetting {
  active: boolean;
  limit: number | null;
}

export interface ZeroPriceSettings {
  b2b: ZeroPriceSetting;
  b2c: ZeroPriceSetting;
}

/** The promotional fields of a product, as far as this module is concerned. */
export interface ZeroPricedProduct {
  zeroPriceType?: ZeroPriceType;
  zeroPriceB2B?: Partial<ZeroPriceSetting>;
  zeroPriceB2C?: Partial<ZeroPriceSetting>;
}

/**
 * The two counter inputs for one target (a cart row, a resident), held as one object so their
 * identity stays stable across change detection instead of being rebuilt on every pass.
 */
export interface ZeroPriceCounterState {
  maxQuantity: number | null;
  canIncrease: (currentValue: number, nextValue: number) => boolean;
}

/** Shared by every target without a limit, so those bindings never change identity either. */
export const UNRESTRICTED_COUNTER: ZeroPriceCounterState = Object.freeze({
  maxQuantity: null,
  canIncrease: () => true,
});

/** Shown when a customer tries to go past a promotional article's per-cart limit. */
export const ZERO_PRICE_LIMIT_REACHED_MESSAGE =
  'Sie haben die maximale Bestellmenge für diesen Artikel erreicht';

/** Legacy `zeroPriceType` value → the areas it released. */
const LEGACY_ACTIVE_AREAS: Record<string, Array<keyof ZeroPriceSettings>> = {
  [ZeroPriceType.B2B]: ['b2b'],
  [ZeroPriceType.B2C]: ['b2c'],
  [ZeroPriceType.B2B_B2C]: ['b2b', 'b2c'],
};

/**
 * Reads a maintained limit. Only whole numbers from 1 upwards are limits; anything else —
 * a blank field, or a value the Cockpit validation should never have let through — counts as
 * unlimited, so a bad value can never block ordering. The server re-checks on submission.
 */
export function toZeroPriceLimit(raw: unknown): number | null {
  if (raw === null || raw === undefined || `${raw}`.trim() === '') {
    return null;
  }
  const parsed = Number(`${raw}`.trim());

  return Number.isInteger(parsed) && parsed >= 1 ? parsed : null;
}

function readSetting(raw?: Partial<ZeroPriceSetting>): ZeroPriceSetting {
  return { active: raw?.active === true, limit: toZeroPriceLimit(raw?.limit) };
}

/**
 * The two areas are only present once CORE serves them. Until then a product arrives with the
 * legacy `zeroPriceType` alone, so fall back to it and treat both areas as unlimited.
 */
function hasAreaFields(product?: ZeroPricedProduct): boolean {
  return (
    product?.zeroPriceB2B?.active !== undefined ||
    product?.zeroPriceB2C?.active !== undefined
  );
}

export function readZeroPriceSettings(
  product?: ZeroPricedProduct
): ZeroPriceSettings {
  if (hasAreaFields(product)) {
    return {
      b2b: readSetting(product.zeroPriceB2B),
      b2c: readSetting(product.zeroPriceB2C),
    };
  }

  const settings: ZeroPriceSettings = {
    b2b: { active: false, limit: null },
    b2c: { active: false, limit: null },
  };
  (LEGACY_ACTIVE_AREAS[product?.zeroPriceType] ?? []).forEach(area => {
    settings[area].active = true;
  });

  return settings;
}

/** Which release type the two areas add up to — drives the existing release dialogs. */
export function effectiveZeroPriceType(
  product?: ZeroPricedProduct
): ZeroPriceType {
  const { b2b, b2c } = readZeroPriceSettings(product);

  if (b2b.active && b2c.active) return ZeroPriceType.B2B_B2C;
  if (b2b.active) return ZeroPriceType.B2B;
  if (b2c.active) return ZeroPriceType.B2C;

  return ZeroPriceType.None;
}

/**
 * The per-cart maximum that applies in the area the customer is shopping in. An article that
 * is not released for that area has no limit of its own — the release check blocks it first.
 */
export function zeroPriceLimitFor(
  product: ZeroPricedProduct,
  isB2BDepartment: boolean
): number | null {
  const { b2b, b2c } = readZeroPriceSettings(product);
  const area = isB2BDepartment ? b2b : b2c;

  return area.active ? area.limit : null;
}
